import { z } from 'zod';
/**
 * Curation request schemas — PRD §6/§7/§10/§23, phases.md Phase 5 task list.
 *
 * Everything here is language-scoped. The Assamese and English homepages are curated
 * separately because they are read by different people (see `curation.ts` in the schema).
 */
const localeSchema = z.enum(['as', 'en']);
export const curationQuerySchema = z.object({
    language: localeSchema.default('as'),
});
/**
 * Hero selection replaces the whole list rather than patching one slot. The hero is an
 * ordered set with a unique `(language, position)` constraint; adding one story at a time
 * would deadlock against that index the moment two positions swap.
 */
export const heroSetSchema = z.object({
    language: localeSchema,
    articleIds: z.array(z.number().int().positive()).max(10),
});
/** PRD §6 — rotation is a property of the hero *section*, stored in `homepage_sections.settings`. */
export const heroSettingsSchema = z.object({
    language: localeSchema,
    autoRotate: z.boolean().optional(),
    /** Seconds. Below 3s the carousel is unusable; above 60s it reads as broken. */
    rotationSeconds: z.number().int().min(3).max(60).optional(),
});
export const FEATURED_PLACEMENTS = ['ranked', 'culture', 'culture_support'];
export const featuredSetSchema = z.object({
    language: localeSchema,
    placement: z.enum(FEATURED_PLACEMENTS),
    articleIds: z.array(z.number().int().positive()).max(20),
});
/**
 * PRD §10: an editor's pin outranks the computed score, and the Phase 16 scoring job must
 * never overwrite it — so pinning is a separate verb from the job's bulk rewrite.
 */
export const trendingPinSchema = z.object({
    language: localeSchema,
    articleId: z.number().int().positive(),
    position: z.number().int().min(0).max(50).optional(),
});
/**
 * Every section the homepage can draw, in the order it draws them by default.
 *
 * The order is not decoration: `setSections` positions any section an editor left out of
 * their payload after the ones they named, in this order (see `curation.service.ts`), so
 * this list is the fallback arrangement. It matches the seed's
 * (`seed/data/curation.ts`) — top story first, video coverage directly under the hero.
 */
export const HOMEPAGE_SECTIONS = [
    'top_story',
    'hero',
    'videos',
    'ranked',
    'categories',
    'latest',
    'trending',
    'culture',
    'live',
    'newsletter',
    'ad',
];
export const homepageSectionsSetSchema = z.object({
    language: localeSchema,
    sections: z
        .array(z.object({
        section: z.enum(HOMEPAGE_SECTIONS),
        isVisible: z.boolean().optional(),
        settings: z.record(z.unknown()).nullish(),
    }))
        .min(1)
        .max(HOMEPAGE_SECTIONS.length),
});
//# sourceMappingURL=curation.js.map